import { KeepAwake } from '@capacitor-community/keep-awake';
import { detectRuntime } from './runtime';

/**
 * Keep the screen awake while a trip is recording.
 *
 *   - Capacitor (Android APK): uses @capacitor-community/keep-awake,
 *     which calls `Window.addFlags(FLAG_KEEP_SCREEN_ON)` natively.
 *     Without this the screen dims after the system idle timeout, the
 *     WebView gets backgrounded, and DeviceMotion + GPS callbacks
 *     stop firing mid-trip.
 *   - Browser (PWA): uses the standard `navigator.wakeLock` API. Some
 *     browsers (older Safari etc.) don't support it; we just no-op
 *     in that case rather than refusing to record.
 *
 * Both paths are best-effort — failures don't abort recording, they
 * just log. Calling acquire() while already held is a no-op.
 */

type BrowserWakeLockSentinel = {
  released: boolean;
  release: () => Promise<void>;
  addEventListener: (type: 'release', cb: () => void) => void;
};

type BrowserNavigatorWithWakeLock = Navigator & {
  wakeLock?: {
    request: (type: 'screen') => Promise<BrowserWakeLockSentinel>;
  };
};

let browserSentinel: BrowserWakeLockSentinel | null = null;
let capacitorHeld = false;

export interface WakeLockResult {
  held: boolean;
  via: 'capacitor' | 'browser-wakelock' | 'unsupported' | 'error';
  detail?: string;
}

export async function acquireWakeLock(): Promise<WakeLockResult> {
  if (detectRuntime() === 'capacitor') {
    try {
      if (!capacitorHeld) {
        await KeepAwake.keepAwake();
        capacitorHeld = true;
      }
      return { held: true, via: 'capacitor' };
    } catch (e) {
      return {
        held: false,
        via: 'error',
        detail: e instanceof Error ? e.message : String(e),
      };
    }
  }

  const nav = navigator as BrowserNavigatorWithWakeLock;
  if (!nav.wakeLock) {
    return { held: false, via: 'unsupported' };
  }
  try {
    if (!browserSentinel || browserSentinel.released) {
      browserSentinel = await nav.wakeLock.request('screen');
      browserSentinel.addEventListener('release', () => {
        // OS released it (typical on tab background). Best-effort: leave
        // browserSentinel as-is so the next acquire() re-requests.
      });
    }
    return { held: true, via: 'browser-wakelock' };
  } catch (e) {
    return {
      held: false,
      via: 'error',
      detail: e instanceof Error ? e.message : String(e),
    };
  }
}

export async function releaseWakeLock(): Promise<void> {
  if (capacitorHeld) {
    try {
      await KeepAwake.allowSleep();
    } catch {
      // best-effort
    }
    capacitorHeld = false;
  }
  if (browserSentinel && !browserSentinel.released) {
    try {
      await browserSentinel.release();
    } catch {
      // best-effort
    }
  }
  browserSentinel = null;
}
